import { useState, useEffect, useRef } from 'react';
import OutboundCallModal from './OutboundCallModal';
import { getOutboundCallDetails, OutboundCallOutcome } from '../../services/outboundCalls';

/**
 * Check if outcome allows retry (e.g., no answer, failed)
 */
function canRetryCall(outcome) {
  return [
    OutboundCallOutcome.NO_ANSWER,
    OutboundCallOutcome.FAILED,
    OutboundCallOutcome.INCOMPLETE,
  ].includes(outcome);
}

/**
 * Check if appointment has a time range (needs scheduling) vs specific time (can confirm)
 * Returns true if appointment has a range and needs a specific time assigned
 */
function hasTimeRange(appointment) {
  const dateRanges = appointment?.date_ranges;
  if (!dateRanges || !Array.isArray(dateRanges) || dateRanges.length === 0) {
    return false;
  }
  // If start !== end, it's a range
  const firstRange = dateRanges[0];
  return firstRange?.start !== firstRange?.end;
}

/**
 * Button component to trigger outbound confirmation calls.
 * Shows different states based on call status.
 * Only displays for NEW_BOOKING appointments with a specific time (not a range).
 */
export default function OutboundCallButton({
  appointment,
  onCallInitiated,
  onCallStatusChange,
  disabled = false,
  className = '',
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [callCompleted, setCallCompleted] = useState(false);
  const [lastOutcome, setLastOutcome] = useState(null);

  const hasPhone =
    appointment?.phone_number || appointment?.callback_phone_number;

  // Check if there's an active call - a call is active if:
  // 1. There's an outbound_call_id
  // 2. AND there's no outcome yet (outcome is only set when call completes)
  // 3. AND we haven't locally detected completion
  const hasActiveCall =
    appointment?.outbound_call_id &&
    !appointment?.outbound_call_outcome &&
    !callCompleted;

  // Only show outbound call button for NEW_BOOKING appointments with a specific time
  const callTag = appointment?.call_tag?.toUpperCase?.() || appointment?.call_tag;
  const isNewBooking = callTag === 'NEW_BOOKING';
  const isTimeRange = hasTimeRange(appointment);

  // Polling interval ref
  const pollingRef = useRef(null);

  // Poll for call status updates when a call is active
  useEffect(() => {
    // Only poll if there's an active call
    if (!hasActiveCall || !appointment?.outbound_call_id) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const pollCallStatus = async () => {
      try {
        console.log('[OutboundCall] Polling status for call:', appointment.outbound_call_id);
        const callDetails = await getOutboundCallDetails(appointment.outbound_call_id);
        console.log('[OutboundCall] Poll response:', callDetails);

        // Check if call has completed
        if (callDetails && ['COMPLETED', 'FAILED'].includes(callDetails.status)) {
          console.log('[OutboundCall] Call completed! Status:', callDetails.status, 'Outcome:', callDetails.outcome);

          // Mark as completed locally to immediately stop polling
          setCallCompleted(true);
          setLastOutcome(callDetails.outcome);

          // Clear polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }

          // Notify parent component of status change
          onCallStatusChange?.({
            outbound_call_id: callDetails.id,
            outbound_call_outcome: callDetails.outcome,
            status: callDetails.status,
          });
        } else {
          console.log('[OutboundCall] Call still in progress, status:', callDetails?.status);
        }
      } catch (error) {
        // Silently handle errors - polling will retry
        console.warn('[OutboundCall] Failed to poll call status:', error);
      }
    };

    // Poll every 5 seconds
    pollingRef.current = setInterval(pollCallStatus, 5000);

    // Also poll immediately on mount
    pollCallStatus();

    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [hasActiveCall, appointment?.outbound_call_id, onCallStatusChange]);

  // Don't show button for non-NEW_BOOKING appointments
  if (!isNewBooking) {
    return <span className="text-gray-400 text-xs">-</span>;
  }

  const handleClick = () => {
    if (!disabled && hasPhone && !hasActiveCall) {
      setIsModalOpen(true);
    }
  };

  const handleCallInitiated = (callData) => {
    // Reset completion state for new call
    setCallCompleted(false);
    setLastOutcome(null);
    onCallInitiated?.(callData);
    setIsModalOpen(false);
  };

  // Determine the current outcome (from appointment or local state)
  const currentOutcome = appointment?.outbound_call_outcome || lastOutcome;

  // Determine button state and appearance
  let buttonContent;
  let buttonClasses;
  let buttonDisabled = disabled;
  let tooltipText = '';

  if (!hasPhone) {
    buttonContent = (
      <>
        <PhoneIcon className="h-3.5 w-3.5" />
        <span>No Phone</span>
      </>
    );
    buttonClasses =
      'bg-gray-50 text-gray-400 cursor-not-allowed ring-1 ring-gray-200';
    buttonDisabled = true;
    tooltipText = 'No phone number available';
  } else if (hasActiveCall) {
    buttonContent = (
      <>
        <SpinnerIcon className="h-3.5 w-3.5 animate-spin" />
        <span>Calling...</span>
      </>
    );
    buttonClasses = 'bg-gradient-to-r from-purple-50 to-violet-50 text-[#3d2398] ring-1 ring-purple-200 cursor-default';
    buttonDisabled = true;
    tooltipText = 'A call is currently in progress';
  } else if (currentOutcome && !canRetryCall(currentOutcome)) {
    // Call completed with non-retryable outcome - don't show button
    return null;
  } else if (canRetryCall(currentOutcome)) {
    // Retryable outcome (NO_ANSWER, FAILED, INCOMPLETE) - show "Retry"
    buttonContent = (
      <>
        <RetryIcon className="h-3.5 w-3.5" />
        <span>Retry</span>
      </>
    );
    buttonClasses =
      'bg-gradient-to-r from-slate-600 to-slate-700 text-white hover:from-slate-700 hover:to-slate-800 ring-1 ring-slate-500/50 shadow-sm shadow-slate-200';
  } else {
    buttonContent = (
      <>
        <PhoneIcon className="h-3.5 w-3.5" />
        <span>Confirm</span>
      </>
    );
    buttonClasses =
      'bg-gradient-to-r from-[#3d2398] to-[#2530a7] text-white hover:from-[#2f1a7a] hover:to-[#1c2485] ring-1 ring-[#3d2398]/50 shadow-sm shadow-[#3d2398]/20';
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={buttonDisabled}
        title={tooltipText}
        className={`
          inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1
          text-[11px] font-semibold tracking-wide
          transition-all duration-200 ease-out
          hover:scale-[1.02] active:scale-[0.98]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3d2398]/40 focus-visible:ring-offset-1
          disabled:opacity-50 disabled:hover:scale-100
          ${buttonClasses}
          ${className}
        `}
      >
        {buttonContent}
      </button>

      <OutboundCallModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        appointment={appointment}
        onCallInitiated={handleCallInitiated}
      />
    </>
  );
}

function PhoneIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" clipRule="evenodd" />
    </svg>
  );
}

function RetryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0v2.43l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z" clipRule="evenodd" />
    </svg>
  );
}

function SpinnerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none">
      <circle
        className="opacity-20"
        cx="10"
        cy="10"
        r="8"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        className="opacity-80"
        fill="currentColor"
        d="M10 2a8 8 0 018 8h-2a6 6 0 00-6-6V2z"
      />
    </svg>
  );
}
